import {
  useEffect,
  useState,
} from "react";

import type {
  Countdown,
} from "./countdown.types";

import {
  formatCountdownDate,
  getCountdownParts,
} from "./countdown.time";

import "./CountdownCard.css";

type Props = {
  countdown: Countdown;
  onEdit: (countdown: Countdown) => void;
  onDelete: (countdownId: string) => void;
};

export function CountdownCard({
  countdown,
  onEdit,
  onDelete,
}: Props) {
  const [now, setNow] = useState(
    () => new Date(),
  );

  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, []);

  const parts = getCountdownParts(
    countdown.eventAt,
    now,
  );

  const values = [
    [parts.years, "year"],
    [parts.months, "month"],
    [parts.days, "day"],
    [parts.hours, "hour"],
    [parts.minutes, "min"],
    [parts.seconds, "sec"],
  ];

  async function shareCountdown() {
    const text =
      `${countdown.title}\n` +
      `${formatCountdownDate(countdown.eventAt)}`;

    if (navigator.share) {
      await navigator.share({
        title: countdown.title,
        text,
      });

      return;
    }

    await navigator.clipboard.writeText(text);

    window.alert("Countdown details copied.");
  }

  return (
    <article className="countdown-card">
      <div className="countdown-card__top">
        <span className="countdown-card__category">
          {countdown.category}
        </span>

        <div className="countdown-card__menu-wrap">
          <button
            className="countdown-card__more"
            type="button"
            aria-label={`More options for ${countdown.title}`}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            ⋮
          </button>

          {menuOpen && (
            <div className="countdown-card__menu">
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  onEdit(countdown);
                }}
              >
                Edit
              </button>

              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  void shareCountdown();
                }}
              >
                Share
              </button>

              <button
                className="countdown-card__delete"
                type="button"
                onClick={() => {
                  setMenuOpen(false);

                  if (
                    window.confirm(
                      `Delete "${countdown.title}"?`,
                    )
                  ) {
                    onDelete(countdown.id);
                  }
                }}
              >
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      <h2 className="countdown-card__title">
        {countdown.title}
      </h2>

      <p className="countdown-card__date">
        {formatCountdownDate(countdown.eventAt)}
      </p>

      {parts.completed ? (
        <div className="countdown-card__completed">
          Event completed
        </div>
      ) : (
        <div className="countdown-card__values">
          {values.map(([value, label]) => (
            <div
              className="countdown-card__unit"
              key={label}
            >
              <strong>
                {label === "hour" ||
                label === "min" ||
                label === "sec"
                  ? String(value).padStart(2, "0")
                  : value}
              </strong>

              <span>
                {value === 1 &&
                label !== "min" &&
                label !== "sec"
                  ? label
                  : label === "min"
                    ? "mins"
                    : label === "sec"
                      ? "secs"
                      : `${label}s`}
              </span>
            </div>
          ))}
        </div>
      )}

      {(countdown.location ||
        countdown.journeyEnabled) && (
        <div className="countdown-card__footer">
          {countdown.location && (
            <span>⌖ {countdown.location}</span>
          )}

          {countdown.journeyEnabled && (
            <span>Journey enabled</span>
          )}
        </div>
      )}
    </article>
  );
}